/**
 * ShareUrlInput — paste a Claude Code / Codex remote share URL.
 *
 * The full URL is encrypted server-side and decrypted just-in-time for
 * the authorized console session, which opens the WebSocket directly
 * to the remote share host. The go-agent is bypassed in this mode.
 *
 * Security notes (also documented in the plan):
 * - The URL is stored as encrypted blob in bot_instances.
 * - The browser handles the URL in memory only and must not log it,
 *   set window.location to it, or persist it.
 * - Tokens embedded in the URL leak into browser history if pasted
 *   into the address bar — we surface a "Rotate token on remote host"
 *   note to remind users.
 */

import { useMemo, useState } from 'react';

interface ShareUrlInputProps {
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}

function parseHost(url: string): string | null {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

function validateUrl(url: string): { ok: boolean; reason?: string } {
  const trimmed = url.trim();
  if (!trimmed) return { ok: false, reason: 'Share URL is required.' };
  let u: URL;
  try { u = new URL(trimmed); }
  catch { return { ok: false, reason: 'Not a valid URL.' }; }
  if (!['ws:', 'wss:', 'http:', 'https:'].includes(u.protocol)) {
    return { ok: false, reason: 'URL must be ws://, wss://, http://, or https://' };
  }
  return { ok: true };
}

export default function ShareUrlInput({ value, onChange, disabled }: ShareUrlInputProps) {
  const [touched, setTouched] = useState(false);
  const host = useMemo(() => parseHost(value), [value]);
  const validation = useMemo(() => validateUrl(value), [value]);

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => { setTouched(true); onChange(e.target.value); }}
        onPaste={() => setTouched(true)}
        placeholder="wss://share.claude.com/s/…?token=…"
        className="w-full px-4 py-2 border border-slate-300 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-xs"
        disabled={disabled}
        spellCheck={false}
        autoComplete="off"
      />
      {host && validation.ok && (
        <div className="flex items-center space-x-2 text-xs">
          <span className="px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium">
            {host}
          </span>
          <span className="text-slate-400 dark:text-slate-500">URL parsed.</span>
        </div>
      )}
      <p className="text-xs text-slate-400 dark:text-slate-500">
        {touched && !validation.ok ? (
          <span className="text-red-500">{validation.reason}</span>
        ) : (
          <>
            Paste a share URL from Claude Code or Codex. The URL is encrypted at rest;
            your browser opens the connection directly to the share host.
          </>
        )}
      </p>
      <p className="text-[11px] text-amber-600 dark:text-amber-500 leading-snug">
        Heads up: tokens embedded in URLs can leak into browser history. Rotate the
        token on the remote host if you've pasted it into other surfaces.
      </p>
    </div>
  );
}
